import React, { useRef, useEffect, useState } from 'react';
import { Dimmer, Dropdown, Input, Loader, Popup, Icon as SemanticIcon, Label } from 'semantic-ui-react';
import { File as FileIcon } from '@styled-icons/boxicons-regular';
import { RemoveCircle as RemoveIcon, CheckmarkCircle, AlertCircle } from '@styled-icons/ionicons-outline';
import * as S from './styles';
import { deleteFilesLinkAxios, postFileAndGetPreSignAxios, updateFileTypeAxios, updateFileLabelAxios, createManualDecisionDocument } from '../../services/file';
import { dangerNotification, successNotification, warningNotification } from '../../services/notification';
import {
  buildFileLabelFromFilename,
  FILE_LABEL_MAX
} from '../../utils/buildFileLabelFromFilename';
import * as Storage from '../../services/storage';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { DateToStringDate, DateToStringDateTime } from '../../services/date';
import { Link } from 'react-router-dom';
import AuthorizationContainer from '../AuthorizationContainer';
import { ActionEnum } from '../../enums/authz-action.enum';
import { FeatureEnum } from '../../enums/authz-feature.enum';
import { getFilesTypesByContext, getFileTranslater } from '../../enums/bond-Insurance-file-context-type.enum';
import { get } from '../../services/storage';
import { fileToBlob, sanitizeFilename, validateFileAccessibility } from '../../utils/fileUtils';
import {
  labelDecisionDocumentOrigin,
  labelDecisionDocumentStatus,
  labelDecisionDocumentType,
  labelInsurerCodeForDecision,
} from '../../utils/decisionDocumentLabels';
import { InsurersCodeEnum } from '../../enums/insurers-code.enum';

export type FileAttachmentProps = {
  createdAt: Date;
  fileLink: string;
  option: string;
  id: string;
  label?: string | null;
  context?: string;
  tag?: {
    name: string;
    tag: string;
    date?: Date;
  };
  decisionDocument?: {
    insurer?: string;
    origin?: string;
    documentType?: string;
    decisionStatus?: string;
    version?: number;
    isCurrent?: boolean;
  } | null;
};

type FileIdentifier = {
  clientId: string;
  file: File;
  name: string;
  customLabel?: string;
};

type FileOption = {
  key: string;
  value: string;
  text: string;
};

const matchesAttachmentFilter = (
  filter: string,
  parts: Array<string | null | undefined>
) => {
  const q = filter.trim().toLowerCase();
  if (!q) return true;
  return parts.some(p => String(p || '').toLowerCase().includes(q));
};

type PendingFileItemProps = {
  file: FileIdentifier;
  outrosDisplayName: string;
  fileOptions: FileOption[];
  firstLoad: boolean;
  fileErrors: Map<string, string>;
  disabled: boolean;
  attachmentFilter: string;
  onTypeChange: (clientId: string, value: string) => void;
  onLabelChange: (clientId: string, label: string) => void;
  onRemove: (file: FileIdentifier) => void;
  onFirstInteraction: () => void;
};

type SavedFileItemProps = FileAttachmentProps & {
  attachmentFilter: string;
  fileOptions: FileOption[];
  disabled: boolean;
  enableEditFileType: boolean;
  enableDecisionDocumentFeatures: boolean;
  onDeleted: (id: string) => void;
  onLabelUpdated: (id: string, label: string) => void;
  onEditFileType: (
    ctrl: FileEditionTypeProps,
    setCtrl: React.Dispatch<React.SetStateAction<FileEditionTypeProps>>
  ) => void;
};

const SavedFileItem = ({
  fileLink,
  option,
  id,
  createdAt,
  label = null,
  context = undefined,
  decisionDocument = null,
  attachmentFilter,
  fileOptions,
  disabled,
  enableEditFileType,
  enableDecisionDocumentFeatures,
  onDeleted,
  onLabelUpdated,
  onEditFileType
}: SavedFileItemProps) => {
  const [ctrl, setCtrl] = useState<FileEditionTypeProps>({
    id,
    oldType: option,
    newType: option,
    edited: false
  });
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editingLabelValue, setEditingLabelValue] = useState(label || '');
  const [savingLabel, setSavingLabel] = useState(false);

  const fileOptionsByContext = context
    ? getFilesTypesByContext(context)
    : fileOptions;

  const typeLabel =
    fileOptionsByContext.find(c => c.key === option)?.value ||
    getFileTranslater(option) ||
    option;
  const isOther = option === 'OTHER';
  const displayTitle = isOther && label ? label : typeLabel;

  if (!matchesAttachmentFilter(attachmentFilter, [displayTitle, typeLabel, label, option])) {
    return null;
  }

  const saveLabel = async () => {
    const trimmed = editingLabelValue.trim().slice(0, FILE_LABEL_MAX);
    if (!trimmed) {
      warningNotification('Atenção', 'Informe um título para o arquivo.');
      return;
    }
    try {
      setSavingLabel(true);
      await updateFileLabelAxios(id, trimmed);
      onLabelUpdated(id, trimmed);
      setIsEditingLabel(false);
      successNotification('Sucesso', 'Título do arquivo atualizado.');
    } catch (err) {
      console.error(err);
      warningNotification('Ops...', 'Erro ao atualizar o título do arquivo.');
    } finally {
      setSavingLabel(false);
    }
  };

  return (
    <S.FileLine>
      <Link to={{ pathname: fileLink }} target="_blank" rel="noopener noreferrer">
        <S.Icon>
          <FileIcon color="green" />
        </S.Icon>
      </Link>

      <S.FileNameCol>
        {isEditingLabel ? (
          <S.LabelEdit>
            <S.LabelEditRow>
              <Input
                fluid
                size="small"
                autoFocus
                value={editingLabelValue}
                maxLength={FILE_LABEL_MAX}
                placeholder="Título do arquivo (ex.: Recibo IPTU)"
                onChange={(_e, { value }) =>
                  setEditingLabelValue(String(value || '').slice(0, FILE_LABEL_MAX))
                }
                onKeyDown={(e: React.KeyboardEvent) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    saveLabel();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setIsEditingLabel(false);
                    setEditingLabelValue(label || '');
                  }
                }}
              />
              <S.LabelEditActions>
                <S.LabelEditAction
                  type="button"
                  $variant="save"
                  title="Salvar"
                  disabled={savingLabel}
                  onClick={saveLabel}
                >
                  <SemanticIcon
                    name={savingLabel ? 'spinner' : 'check'}
                    loading={savingLabel}
                    fitted
                  />
                </S.LabelEditAction>
                <S.LabelEditAction
                  type="button"
                  $variant="cancel"
                  title="Cancelar"
                  disabled={savingLabel}
                  onClick={() => {
                    setIsEditingLabel(false);
                    setEditingLabelValue(label || '');
                  }}
                >
                  <SemanticIcon name="close" fitted />
                </S.LabelEditAction>
              </S.LabelEditActions>
            </S.LabelEditRow>
            <S.LabelHint>
              {editingLabelValue.length}/{FILE_LABEL_MAX} · Enter para salvar
            </S.LabelHint>
          </S.LabelEdit>
        ) : (
          <S.FileTitleRow>
            <Link
              to={{ pathname: fileLink }}
              target="_blank"
              rel="noopener noreferrer"
              style={{ minWidth: 0, overflow: 'hidden' }}
            >
              <S.FileTitle
                title={displayTitle}
                style={{ fontWeight: isOther && label ? 600 : 400 }}
              >
                {displayTitle}
              </S.FileTitle>
            </Link>
            {isOther && label && (
              <Label size="mini" basic color="grey" style={{ flexShrink: 0 }}>
                Outros
              </Label>
            )}
            {isOther && !disabled && (
              <SemanticIcon
                name="pencil"
                color="grey"
                style={{ cursor: 'pointer', flexShrink: 0 }}
                title="Editar título"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingLabelValue(label || '');
                  setIsEditingLabel(true);
                }}
              />
            )}

            {enableDecisionDocumentFeatures && decisionDocument && (
              <Popup
                position="top center"
                wide="very"
                hoverable
                trigger={
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      cursor: 'help',
                      flexShrink: 0
                    }}
                  >
                    <SemanticIcon
                      name="info circle"
                      color={decisionDocument.isCurrent ? 'green' : 'grey'}
                    />
                    {!decisionDocument.isCurrent && (
                      <span
                        style={{
                          fontSize: '0.75rem',
                          color: '#999',
                          marginLeft: 3,
                          fontStyle: 'italic'
                        }}
                      >
                        (arquivada)
                      </span>
                    )}
                  </span>
                }
                content={
                  <div style={{ fontSize: '0.9rem', lineHeight: 1.6, maxWidth: 300 }}>
                    <p
                      style={{
                        margin: '0 0 8px 0',
                        paddingBottom: 6,
                        borderBottom: '1px solid #eee'
                      }}
                    >
                      <strong style={{ color: '#333' }}>
                        {labelDecisionDocumentType(decisionDocument.documentType)}
                      </strong>
                    </p>
                    <p style={{ margin: '0 0 4px 0' }}>
                      <strong>Origem:</strong>{' '}
                      {labelDecisionDocumentOrigin(decisionDocument.origin)}
                    </p>
                    <p style={{ margin: '0 0 4px 0' }}>
                      <strong>Seguradora:</strong>{' '}
                      {labelInsurerCodeForDecision(decisionDocument.insurer)}
                    </p>
                    <p style={{ margin: 0 }}>
                      <strong>Situação:</strong>{' '}
                      {labelDecisionDocumentStatus(decisionDocument.decisionStatus)}
                    </p>
                  </div>
                }
              />
            )}
          </S.FileTitleRow>
        )}
      </S.FileNameCol>

      <S.FileMeta>{DateToStringDateTime(createdAt)}</S.FileMeta>

      <S.Select
        placeholder="Identificacao do documento"
        onChange={e => {
          setCtrl({
            ...ctrl,
            newType: e.target.value,
            edited: true
          });
        }}
      >
        {fileOptionsByContext.map(fileType => (
          <option
            value={fileType.value}
            key={fileType.key}
            selected={fileType.key === ctrl.newType}
            disabled={!enableEditFileType}
          >
            {' '}
            {fileType.value}{' '}
          </option>
        ))}
      </S.Select>

      <S.FileActions>
        {!disabled && (
          <AuthorizationContainer action={ActionEnum.delete} feature={FeatureEnum.files}>
            <S.Icon onClick={() => onDeleted(id)}>
              <RemoveIcon color="red" />
            </S.Icon>
          </AuthorizationContainer>
        )}

        {enableEditFileType && (
          <S.Icon onClick={() => onEditFileType(ctrl, setCtrl)}>
            <CheckmarkCircle color={ctrl.edited ? 'green' : 'gray'} />
          </S.Icon>
        )}
      </S.FileActions>
    </S.FileLine>
  );
};

const PendingFileItem = ({
  file: fileIdentified,
  outrosDisplayName,
  fileOptions,
  firstLoad,
  fileErrors,
  disabled,
  attachmentFilter,
  onTypeChange,
  onLabelChange,
  onRemove,
  onFirstInteraction
}: PendingFileItemProps) => {
  const isOther =
    fileIdentified.name === outrosDisplayName || fileIdentified.name === 'OTHER';

  if (
    !matchesAttachmentFilter(attachmentFilter, [
      fileIdentified.file.name,
      fileIdentified.name,
      fileIdentified.customLabel,
      isOther ? 'Outros' : ''
    ])
  ) {
    return null;
  }

  return (
    <S.FileLine>
      <S.Icon>
        {fileErrors.has(fileIdentified.file.name) ? (
          <Popup
            content={fileErrors.get(fileIdentified.file.name)}
            trigger={<AlertCircle color="orange" />}
            position="top center"
          />
        ) : (
          <FileIcon color="blue" />
        )}
      </S.Icon>
      <S.FileNameCol>
        <S.FileTitle title={fileIdentified.file.name}>
          {fileIdentified.file.name}
        </S.FileTitle>
        {isOther && (
          <S.LabelEdit style={{ marginTop: 6 }}>
            <Input
              fluid
              size="small"
              value={fileIdentified.customLabel || ''}
              maxLength={FILE_LABEL_MAX}
              placeholder="Título do arquivo (ex.: Recibo IPTU)"
              onChange={(_e, { value }) =>
                onLabelChange(fileIdentified.clientId, String(value || ''))
              }
              onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
              onKeyUp={(e: React.KeyboardEvent) => e.stopPropagation()}
            />
            <S.LabelHint>
              {(fileIdentified.customLabel || '').length}/{FILE_LABEL_MAX}
            </S.LabelHint>
          </S.LabelEdit>
        )}
      </S.FileNameCol>
      <S.FileMeta>
        {(fileIdentified.file.size / 1024 / 1024).toFixed(2)} MB
      </S.FileMeta>
      <S.SelectDiv>
        <Dropdown
          search
          clearable
          selection
          value={firstLoad ? undefined : fileIdentified.name || undefined}
          options={fileOptions}
          placeholder={'Selecionar...'}
          onChange={(_e, { value }) => {
            onFirstInteraction();
            onTypeChange(fileIdentified.clientId, value as string);
          }}
        />
      </S.SelectDiv>
      {!disabled && (
        <AuthorizationContainer action={ActionEnum.delete} feature={FeatureEnum.files}>
          <S.Icon onClick={() => onRemove(fileIdentified)}>
            <RemoveIcon color="red" />
          </S.Icon>
        </AuthorizationContainer>
      )}
    </S.FileLine>
  );
};

type fileTypeProps = {
  name: string;
  key: string;
  required?: boolean;
};

type FileEditionTypeProps = {
  id: string;
  oldType: string;
  newType: string;
  edited: boolean;
};

export type FileUploaderProps = {
  tableName: string;
  registerId: string;
  controlSubmitFiles?: boolean;
  minimumAmountOfFiles?: number;
  fileIdentifiers?: fileTypeProps[];
  filesAttachs?: FileAttachmentProps[];
  callback: (_: any) => void;
  brokerOnly?: boolean;
  disabled?: boolean;
  enableDecisionDocumentFeatures?: boolean;
};

const FileUploader = ({
  tableName,
  registerId,
  controlSubmitFiles = false,
  minimumAmountOfFiles = 0,
  filesAttachs,
  fileIdentifiers = [
    {
      name: 'Outros',
      key: 'OTHER',
      required: false
    },
    {
      name: 'Apólice',
      key: 'POLICY',
      required: false
    },
    {
      name: 'Carta de Aprovação Porto Essencial',
      key: 'PORTO_ESSENCIAL_APROVAL_LETTER',
      required: false
    },
    {
      name: 'Carta de aprovação condições',
      key: 'PORTO_ESSENCIAL_BUDGETS_OFFERS',
      required: false
    },
    {
      name: 'Contrato de locação',
      key: 'RENT_CONTRACT',
      required: false
    },
    {
      name: 'Cotação Porto Essencial',
      key: 'PORTO_ESSENCIAL_APROVAL_LETTER_BUDGETS',
      required: false
    },
    {
      name: 'Cotação condições',
      key: 'PORTO_ESSENCIAL_APPROVAL_OFFERS',
      required: false
    },
    {
      name: 'Endosso de correção',
      key: 'CORRECTION_ENDORSEMENT',
      required: false
    },
  ],
  callback,
  brokerOnly,
  disabled = false,
  enableDecisionDocumentFeatures = false,
}: FileUploaderProps) => {
  const [files, setFiles] = useState<FileIdentifier[]>();
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState<FileAttachmentProps[]>(filesAttachs || []);
  const [enableEditFileType, setEnableEditFileType] = useState<boolean>(false);
  // Estado para seleção de seguradora em upload manual de decisão
  const [selectedInsurer, setSelectedInsurer] = useState<string>('');
  // Estado para rastrear arquivos com erro - Map de nome do arquivo para mensagem de erro
  const [fileErrors, setFileErrors] = useState<Map<string, string>>(new Map());
  const [attachmentFilter, setAttachmentFilter] = useState('');

  const fileOptions = fileIdentifiers.map(fileIdentifier => ({
    key: fileIdentifier.key,
    value: fileIdentifier.name,
    text: fileIdentifier.name
  }));

  const outrosDisplayName =
    fileIdentifiers.find(fi => fi.key === 'OTHER')?.name || 'Outros';

  const insurerOptions = [
    { key: '', value: '', text: 'Selecione a seguradora...' },
    ...Object.values(InsurersCodeEnum).map(code => ({
      key: code,
      value: code,
      text: labelInsurerCodeForDecision(code) || code,
    })),
  ];

  // Verifica se algum arquivo anexado é uma carta de decisão
  const hasDecisionLetter = files?.some(file => {
    const fileKey = fileIdentifiers.find(fi => fi.name === file.name)?.key;
    return fileKey && (
      fileKey.includes('APPROVAL') ||
      fileKey.includes('REJECTION') ||
      fileKey.includes('RESPONSE')
    );
  });

  const userRole = get('role');

  useEffect(() => {
    if (filesAttachs) {
      setAttachedFiles(filesAttachs);
      setEnableEditFileType((
        userRole === UserRolesEnum.brokerAdmin ||
        userRole === UserRolesEnum.brokerAnalyst
      ));
    }
  }, [filesAttachs, userRole]);

  let ref: any = useRef(null);

  function handleAddFile(fileList: FileList) {
    if (!fileList) {
      return;
    }
    const array = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file.size > 50 * 1024 * 1024) {
        warningNotification(
          'Limite excedido',
          `O arquvio ${file.name.substr(
            0,
            10
          )} é muito grande. Nosso limite interno é de 50MB.`
        );
        return;
      }

      array.push({
        clientId: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${i}`,
        file,
        name: outrosDisplayName,
        customLabel: buildFileLabelFromFilename(file.name)
      });
    }
    setFiles((files || []).concat(array));
  }

  function handlePendingTypeChange(clientId: string, value: string) {
    const nextIsOther = value === outrosDisplayName || value === 'OTHER';
    setFiles(prev =>
      (prev || []).map(file =>
        file.clientId !== clientId
          ? file
          : {
              ...file,
              name: value,
              customLabel: nextIsOther
                ? file.customLabel || buildFileLabelFromFilename(file.file.name)
                : undefined
            }
      )
    );
  }

  function handlePendingLabelChange(clientId: string, label: string) {
    setFiles(prev =>
      (prev || []).map(file =>
        file.clientId !== clientId
          ? file
          : { ...file, customLabel: label.slice(0, FILE_LABEL_MAX) }
      )
    );
  }

  function handleRemoveFile(toRemove: FileIdentifier) {
    const array = files?.filter(fileIdentified => fileIdentified !== toRemove);
    setFiles(array);
    // Limpar erro associado ao arquivo removido
    if (fileErrors.has(toRemove.file.name)) {
      const newErrors = new Map(fileErrors);
      newErrors.delete(toRemove.file.name);
      setFileErrors(newErrors);
    }
  }

  useEffect(() => {
    async function sumbitFiles() {
      if (!files) return;

      // Verificar se há arquivos com erros pendentes
      if (fileErrors.size > 0) {
        const errorFileNames = Array.from(fileErrors.keys()).join(', ');
        warningNotification(
          'Arquivos com problema',
          `Os seguintes arquivos apresentam erros e precisam ser removidos: ${errorFileNames}`
        );
        callback({ error: true }); // Destrava o botão do pai
        return;
      }

      if ((files?.length || 0) < minimumAmountOfFiles) {
        warningNotification(
          'Faltam arquivos',
          'Para submeter esse formulário,' +
          ' você precisa submeter pelo menos ' +
          minimumAmountOfFiles +
          ' arquivos.'
        );
        callback({ error: true }); // Destrava o botão do pai
        return;
      }

      const requireds = fileIdentifiers.filter(fi => fi.required === true);
      const intersection = requireds.filter(
        r =>
          files.filter(c => fileIdentifiers.find(d => d.name === c.name)?.key === r.name)
            .length > 0
      );
      if (intersection.length > 0) {
        intersection.forEach(req => {
          warningNotification('Ops', `Por favor, anexe um arquivo do tipo: ${req.name}`);
        });
      }

      const missingOtherLabels = files.some(file => {
        const fileKey = fileIdentifiers.find(fi => fi.name === file.name)?.key ||
          (file.name === 'OTHER' ? 'OTHER' : undefined);
        return fileKey === 'OTHER' && !String(file.customLabel || '').trim();
      });
      if (missingOtherLabels) {
        warningNotification(
          'Atenção',
          'Informe um título para cada arquivo do tipo Outros (máx. 30 caracteres).'
        );
        callback({ error: true });
        return;
      }

      try {
        setLoading(true);
        const responses = [];
        const createdFileIds: string[] = []; // IDs dos arquivos criados para possível rollback
        const newErrors = new Map<string, string>();
        const decisionDocResults: any[] = [];

        for (const file of files) {
          let fileId: string | null = null;

          try {
            await validateFileAccessibility(file.file);

            const fileKey =
              fileIdentifiers.find(fi => fi.name === file.name)?.key || 'OTHER';
            const trimmedLabel = String(file.customLabel || '').trim();

            const response = await postFileAndGetPreSignAxios({
              filename: sanitizeFilename(file.file.name),
              contentType: file.file.type,
              referencedTableName: tableName,
              referencedTableId: registerId,
              name: fileKey,
              label: fileKey === 'OTHER' ? trimmedLabel || null : null
            });

            fileId = response.id;
            createdFileIds.push(fileId);

            const blob = await fileToBlob(file.file);

            const uploadResponse = await fetch(response.uploadURL, {
              method: 'PUT',
              body: blob
            });

            if (!uploadResponse.ok) {
              throw new Error(`Falha no upload: ${uploadResponse.status} ${uploadResponse.statusText}`);
            }

            responses.push({
              id: response.id,
              contextType:
                fileKey === 'OTHER' && trimmedLabel ? trimmedLabel : file.name,
              label: fileKey === 'OTHER' ? trimmedLabel : undefined
            });

            // Se for decision document e seguradora selecionada, registrar manualmente
            if (enableDecisionDocumentFeatures && selectedInsurer && fileIdentifiers.find(fi => fi.name === file.name)?.key) {
              const fileKey = fileIdentifiers.find(fi => fi.name === file.name)?.key;
              const isApprovalLetter = ['APPROVAL_LETTER', 'PORTO_ESSENCIAL_APROVAL_LETTER'].includes(fileKey || '');
              const isRejectionLetter = ['REJECTION_LETTER', 'RESPONSE_LETTER'].includes(fileKey || '');

              if (isApprovalLetter || isRejectionLetter) {
                try {
                  const decisionResult = await createManualDecisionDocument(registerId, {
                    fileId: response.id,
                    insurer: selectedInsurer,
                    documentType: isApprovalLetter ? 'approval_letter' : 'rejection_letter',
                    decisionStatus: isApprovalLetter ? 'approved' : 'rejected',
                  });
                  decisionDocResults.push(decisionResult);
                } catch (decisionError) {
                  console.warn('Erro ao registrar documento de decisão manual:', decisionError);
                }
              }
            }

          } catch (uploadError) {
            // Se falhou após criar a entrada no banco, fazer rollback
            if (fileId) {
              try {
                console.log(`[Upload] Rollback: removendo entrada órfã ${fileId}`);
                await deleteFilesLinkAxios(fileId);
              } catch (deleteError) {
                console.error(`[Upload] Falha ao remover entrada órfã ${fileId}:`, deleteError);
              }
            }

            // Registrar erro para este arquivo
            const errorMessage = (uploadError as Error)?.message ||
              `Erro ao processar o arquivo "${file.file.name}"`;
            newErrors.set(file.file.name, errorMessage);
          }
        }

        // Se houve erros, atualizar estado e mostrar warning
        if (newErrors.size > 0) {
          setFileErrors(prev => {
            const merged = new Map(prev);
            newErrors.forEach((value, key) => merged.set(key, value));
            return merged;
          });

          const errorFileNames = Array.from(newErrors.keys()).join(', ');
          warningNotification(
            'Alguns arquivos não puderam ser enviados',
            `Remova os arquivos com problema e adicione novamente: ${errorFileNames}`
          );
          // Modal permanece aberta para o usuário corrigir o problema
          callback({ error: true }); // Destrava o botão do pai
        } else if (responses.length > 0) {
          callback(responses);
          const decisionCount = decisionDocResults.length;
          if (decisionCount > 0) {
            const insurerLabel = selectedInsurer ? labelInsurerCodeForDecision(selectedInsurer) : 'seguradora';
            successNotification(
              'Cartas registradas',
              `${decisionCount} documento(s) de decisão da ${insurerLabel} ${decisionCount === 1 ? 'foi registrado' : 'foram registrados'} como vigente.`
            );
          } else {
            successNotification('Sucesso', 'Arquivos enviados com sucesso!');
          }
        }
      } catch (err) {
        // Erro inesperado geral
        const errorMessage = (err as Error)?.message || 'Ocorreu um erro ao tentar realizar o upload dos arquivos. Tente novamente';
        warningNotification(
          'Erro ao efetuar upload',
          errorMessage
        );
        console.log('ERRO DE ARQUIVO', err);
        // Modal permanece aberta para o usuário corrigir o problema
        callback({ error: err }); // Destrava o botão do pai
      } finally {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    controlSubmitFiles && sumbitFiles();
  }, [controlSubmitFiles]);

  async function SoftDeleteFile(id: string) {
    try {
      if (window.confirm('Deseja deletar este arquivo?')) {
        setLoading(true);
        await deleteFilesLinkAxios(id);
        const array = attachedFiles?.filter(fileIdentified => fileIdentified.id !== id);
        setAttachedFiles(array);
        successNotification('Sucesso!', 'Arquivo deletado com sucesso!');
      }
    } catch (err) {
      console.log(err);
      warningNotification('Ops...', 'Erro ao deletar arquivo.');
    } finally {
      setLoading(false);
    }
  };

  async function EditFileType(
    ctrl: FileEditionTypeProps,
    setCtrl: React.Dispatch<React.SetStateAction<FileEditionTypeProps>>
  ) {
    try {
      const newType = fileOptions.find(c => c.value === ctrl.newType);

      if (newType) {
        await updateFileTypeAxios(ctrl.id, registerId, ctrl.oldType, newType.key);

        setCtrl({
          id: ctrl.id,
          oldType: ctrl.newType,
          newType: ctrl.newType,
          edited: false,
        });
      } else {
        warningNotification('Ops...', 'Erro ao alterar tipo do arquivo.');
      }

      successNotification('Sucesso!', 'Tipo do arquivo alterado com sucesso!');
    } catch (err) {
      console.log(err);
      warningNotification('Ops...', 'Erro ao alterar tipo do arquivo.');
    }
  };

  const handleLabelUpdated = (fileId: string, nextLabel: string) => {
    setAttachedFiles(prev =>
      prev.map(f => (f.id === fileId ? { ...f, label: nextLabel } : f))
    );
  };

  const renderUniqueTags = () => {
    const uniqueTagsArray = Array.from(new Set(attachedFiles.map(file => file?.tag?.name).filter(Boolean)));
    return uniqueTagsArray.map((tagName, index) => {
      const filesWithTag = attachedFiles.filter(file => file?.tag?.name === tagName);
      return (
        <S.FileWrapper key={index}>
          <S.LabelSavedFiles>Arquivos salvos em: {tagName}</S.LabelSavedFiles>
          {filesWithTag.map(file => (
            <SavedFileItem
              key={file.id}
              fileLink={file.fileLink}
              option={file.option}
              id={file.id}
              createdAt={file.createdAt}
              label={file.label}
              context={file.tag?.tag}
              decisionDocument={file.decisionDocument}
              attachmentFilter={attachmentFilter}
              fileOptions={fileOptions}
              disabled={disabled}
              enableEditFileType={enableEditFileType}
              enableDecisionDocumentFeatures={enableDecisionDocumentFeatures}
              onDeleted={SoftDeleteFile}
              onLabelUpdated={handleLabelUpdated}
              onEditFileType={EditFileType}
            />
          ))}
        </S.FileWrapper>
      );
    });
  };

  const renderFilesWithoutTags = () => {
    return (
      <S.FileWrapper>
        <S.LabelSavedFiles>Arquivos salvos</S.LabelSavedFiles>
        {attachedFiles.map(file => (
          <SavedFileItem
            key={file.id}
            fileLink={file.fileLink}
            option={file.option}
            id={file.id}
            createdAt={file.createdAt}
            label={file.label}
            decisionDocument={file.decisionDocument}
            attachmentFilter={attachmentFilter}
            fileOptions={fileOptions}
            disabled={disabled}
            enableEditFileType={enableEditFileType}
            enableDecisionDocumentFeatures={enableDecisionDocumentFeatures}
            onDeleted={SoftDeleteFile}
            onLabelUpdated={handleLabelUpdated}
            onEditFileType={EditFileType}
          />
        ))}
      </S.FileWrapper>
    );
  };

  return (
    <S.Wrapper>
      {(attachedFiles?.length > 0 || (files && files.length > 0)) && (
        <S.SearchBar>
          <Input
            fluid
            icon="search"
            iconPosition="left"
            placeholder="Buscar por nome, tipo ou título…"
            value={attachmentFilter}
            onChange={(_e, { value }) => setAttachmentFilter(String(value || ''))}
          />
        </S.SearchBar>
      )}

      {attachedFiles?.length > 0 && (attachedFiles[0].tag ? renderUniqueTags() : renderFilesWithoutTags())}

      {files && files?.length > 0 && (
        <S.FileWrapper>
          <S.LabelSavedFiles>Arquivos anexados</S.LabelSavedFiles>
          {files.map(file => (
            <PendingFileItem
              key={file.clientId}
              file={file}
              outrosDisplayName={outrosDisplayName}
              fileOptions={fileOptions}
              firstLoad={firstLoad}
              fileErrors={fileErrors}
              disabled={disabled}
              attachmentFilter={attachmentFilter}
              onTypeChange={handlePendingTypeChange}
              onLabelChange={handlePendingLabelChange}
              onRemove={handleRemoveFile}
              onFirstInteraction={() => setFirstLoad(false)}
            />
          ))}
        </S.FileWrapper>
      )}

      {brokerOnly && !disabled && (
        <S.AddFileArea>
          <Dimmer active={loading}>
            <Loader indeterminate>Aguarde...</Loader>
          </Dimmer>
          <AuthorizationContainer action={ActionEnum.upload} feature={FeatureEnum.files}>
            <S.InputButton type="button" value="Adicionar Arquivo" onClick={() => ref && ref.click()} />

            {enableDecisionDocumentFeatures && hasDecisionLetter && (
              <div style={{ marginTop: 12, marginBottom: 8, padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: 6, border: '1px solid #ddd', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <label style={{ fontSize: '0.9rem', color: '#555', marginRight: 12, display: 'inline-block', minWidth: 180 }}>
                  Seguradora (para cartas de decisão):
                </label>
                <Dropdown
                  selection
                  clearable
                  placeholder='Selecionar seguradora...'
                  value={selectedInsurer || ''}
                  options={insurerOptions}
                  onChange={(e, { value }) => {
                    setSelectedInsurer(value as string);
                  }}
                  style={{ minWidth: 220, fontSize: '0.9rem' }}
                />
                {selectedInsurer && (
                  <span style={{ marginLeft: 8, fontSize: '0.85rem', color: '#666' }}>
                    Os arquivos identificados como carta serão vinculados à {labelInsurerCodeForDecision(selectedInsurer)}
                  </span>
                )}
              </div>
            )}

            <S.InputFile
              type="file"
              name="file"
              disabled={disabled}
              multiple
              ref={input => {
                ref = input;
              }}
              onChange={e => e.target.files && handleAddFile(e.target.files)}
              accept="image/png, image/jpeg, image/webp, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf, application/vnd.oasis.opendocument.text"
            />
          </AuthorizationContainer>
        </S.AddFileArea>
      )}
    </S.Wrapper>
  );
};
export default FileUploader;
